import type { TFunction } from 'i18next';

import { Download, Eye } from '@unified-checkout/design-system/icons/index';
import type { IButton } from 'libs/design-system/src/types/Button/button';
import type {
  IColumnsMap,
  IFilterMap,
} from 'libs/design-system/src/types/data_table';

import { EMPTY_VALUE } from '@app/dashboard/constants/shared';

import {
  bankAccountNumberMask,
  isUAECountry,
} from '@app/dashboard/views/pages/inner_layout/BalanceAndTransfers/utils';

export const TRANSFERS_STATUS = {
  FAILED: 'failed',
  REJECT_INSTANT: 'reject_instant',
  RETURNED_BY_BANK: 'returned_by_bank',
  INITIATED: 'initiated',
  ON_ITS_WAY: 'on_its_way',
  SETTLED: 'settled',
  IN_REVIEW: 'in_review',
};

export const COLUMNS_MAP = ({
  t,
  lang,
  handlers,
}: {
  t: TFunction;
  lang: 'en' | 'ar';
  handlers: Record<string, IButton>;
}): IColumnsMap => [
  {
    id: 'transfer_id',
    name: t('innerLayout.balanceTransfers.table.headers.transferId'),
    type: 'text',
    key: 'transfer_id',
  },
  {
    id: 'net_amount',
    name: t('innerLayout.balanceTransfers.table.headers.amount'),
    type: 'amount',
    key: 'net_amount',
    additionalText: {
      key: 'currency',
      placement: lang === 'en' ? 'start' : 'end',
    },
  },
  {
    id: 'destination',
    name: t('innerLayout.balanceTransfers.table.headers.destination'),
    type: 'text',
    key: 'destination',
    format(_, value) {
      return bankAccountNumberMask(value);
    },
  },
  {
    id: 'status',
    name: t('innerLayout.balanceTransfers.table.headers.status'),
    type: 'badge',
    key: 'status.key',
    label: {
      initiated: {
        color: 'grey',
        text: t('innerLayout.balanceTransfers.status.initiated'),
      },
      on_its_way: {
        color: 'yellow',
        text: t('innerLayout.balanceTransfers.status.onItsWay'),
      },
      settled: {
        color: 'green',
        text: t('innerLayout.balanceTransfers.status.settled'),
      },
      in_review: {
        color: 'primary',
        text: t('innerLayout.balanceTransfers.status.inReview'),
      },
      reject_instant: {
        color: 'red',
        text: t('innerLayout.balanceTransfers.status.failed'),
      },
      failed: {
        color: 'red',
        text: t('innerLayout.balanceTransfers.status.failed'),
      },
      returned_by_bank: {
        color: 'red',
        text: t('innerLayout.balanceTransfers.status.failed'),
      },
    },
    labelComparingValue: 'status.key',
  },
  ...((isUAECountry()
    ? [
        {
          id: 'bank_ref_no',
          name: t('innerLayout.balanceTransfers.table.headers.bankRefNo'),
          type: 'text',
          key: 'bank_ref_no',
          format(data, value) {
            if (
              [TRANSFERS_STATUS.INITIATED, TRANSFERS_STATUS.IN_REVIEW].includes(
                data?.status?.key
              )
            ) {
              return EMPTY_VALUE;
            }

            return value;
          },
        },
      ]
    : []) as IColumnsMap),
  {
    id: 'settlement_date',
    name: t('innerLayout.balanceTransfers.table.headers.settlementDate'),
    type: 'date',
    key: 'settlement_date',
    hintText: 'settlement_date',
  },

  ...((isUAECountry()
    ? [
        {
          id: 'ach',
          name: t('innerLayout.balanceTransfers.table.headers.ach'),
          type: 'action_btns',
          key: 'ach',
          actionBtnsProps: (details: any) => {
            const selectedRow =
              details?.transfer_id &&
              handlers?.downloadAch?.id === details?.transfer_id;

            const isLoading = handlers?.downloadAch?.loading;

            return [
              {
                leadingIcon: isLoading && selectedRow ? undefined : Download,
                variant: 'link',
                color: 'secondary',
                size: 'sm',
                ...handlers.downloadAch,
                disabled:
                  !details?.is_ach_available || handlers?.downloadAch?.disabled,
                loading: isLoading && selectedRow,
              },
            ];
          },
        },
      ]
    : []) as IColumnsMap),
  {
    id: 'actions',
    name: t('innerLayout.balanceTransfers.table.headers.action'),
    type: 'action_btns',
    key: 'actions',
    actionBtnsProps: (details: any) => [
      {
        leadingIcon: Eye,
        variant: 'link',
        color: 'secondary',
        id: 'view_details',
        size: 'sm',
        ...handlers.openDetails,
      },
    ],
  },
];

export const FILTER_TYPES: Record<string, IFilterMap['type']> = {
  dropdown: 'select',
  multi_selection_dropdown: 'multi_selection',
  text_box: 'text',
  phone_number: 'phone',
};

export const PARAMS = {
  ACCOUNT: 'account_id',
  CURRENCY: 'currency',
};

export const ALLOWED_PATHS = ['/balance', '/transfers', '/transfer/'];

export const SESSION_KEYS = {
  SWITCHED_COUNT: 'switched_count',
  TIME_SPENT: 'time_spent',
};
